import _ from 'lodash';
import axios from 'axios';
import configService from '@/services/configService';
import { unwrapData } from '@/services/apiEnvelope';

export const QMS_S3_PUBLIC_URL = 'https://spencer-qms.s3.ap-south-1.amazonaws.com';

/** Backend: handleSuccess(res, '', signedUrl) => { success, data: signedUrl } */
export function parseQmsSignedUrl(body) {
  if (!body) {
    return '';
  }
  if (typeof body === 'string') {
    return body;
  }
  return body.data || '';
}

/** Public URL after upload (Key = filename query param, bucket spencer-qms). */
export function toQmsPublicImageUrl(filename) {
  return `${QMS_S3_PUBLIC_URL}/${filename}`;
}

/** Strip presigned query params; backend returns the object URL before `?`. */
export function toQmsPublicImageUrlFromSignedUrl(signedUrl) {
  if (!signedUrl || typeof signedUrl !== 'string') {
    return '';
  }
  return signedUrl.split('?')[0];
}

export const QMS_ATTACHMENT_ACCEPT = 'image/*,.jpg,.jpeg,.png,.gif,.webp,.pdf,application/pdf';

export function isAllowedQmsAttachmentFile(file) {
  if (!file) {
    return false;
  }
  if (file.type && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
    return true;
  }
  const ext = String(file.name || '')
    .split('.')
    .pop()
    .toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(ext);
}

export function isQmsImageUrl(url) {
  if (!url) {
    return false;
  }
  const path = String(url).split('?')[0].toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp)$/.test(path);
}

export function getQmsAttachmentFileName(url) {
  if (!url) {
    return 'attachment';
  }
  const path = String(url).split('?')[0];
  const parts = path.split('/');
  return decodeURIComponent(parts[parts.length - 1] || 'attachment');
}

export async function downloadQmsAttachment(url, fileName = '') {
  if (!url) {
    return;
  }
  const resolvedName = fileName || getQmsAttachmentFileName(url);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Download failed');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = resolvedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (_e) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = resolvedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export default {
  async changeEscalationLevel({ id, escalateTo, escalateRemarks = '' }) {
    // Use fresh API values: list rows omit mobile and transform attachments/dates.
    const ticket = await this.getOne({ id });
    if (!ticket || typeof ticket !== 'object' || Array.isArray(ticket) || !Object.keys(ticket).length) {
      throw new Error('Unable to load the ticket for escalation. Please try again.');
    }
    // Match the existing QmsFormBox update payload, without resending server metadata.
    const payload = _.pick(ticket, [
      'isClosed', 'employeeRefImages', 'closureRefImages', 'employeeCode',
      'storeCode', 'employeeName', 'employeeMobile', 'department',
      'queryType', 'queryDescription', 'closureRemarks'
    ]);
    return axios.patch(`${configService.get('apiUrl')}/qms-by-role/${encodeURIComponent(id)}`, {
      ...payload,
      escalateTo,
      escalationRemarks: escalateRemarks
    }).then(response => unwrapData(response.data));
  },
  async list({ query = {} } = {}) {
    const pickedQuery = _.pick(query, [
      'q',
      'page',
      'page_size',
      'filter',
      'search',
      'searchkey',
      'startdate',
      'enddate',
      'mobile'
    ]);
    const cleaned = _.pickBy(pickedQuery, v => v !== '' && v !== undefined && v !== null);
    let url = `${configService.get('apiUrl')}/qms`;
    if (!_.isEmpty(cleaned)) {
      const params = new URLSearchParams();
      Object.keys(cleaned).forEach(key => {
        params.append(key, cleaned[key]);
      });
      url += `?${params.toString()}`;
    }

    return axios.get(url, {}).then(response => unwrapData(response.data));
  },
  async getQmsListByRole({ query = {} } = {}) {
    const pickedQuery = _.pick(query, [
      'q',
      'page',
      'page_size',
      'filter',
      'search',
      'searchkey',
      'startdate',
      'enddate',
      'isQMSAdmin'
    ]);
    const cleaned = _.pickBy(
      pickedQuery,
      (value, key) => value !== '' && value !== undefined && value !== null && (key !== 'isQMSAdmin' || value === true || value === 'true')
    );
    let url = `${configService.get('apiUrl')}/qms-by-role`;
    if (!_.isEmpty(cleaned)) {
      const params = new URLSearchParams();
      Object.keys(cleaned).forEach(key => {
        params.append(key, cleaned[key]);
      });
      url += `?${params.toString()}`;
    }

    return axios.get(url, {}).then(response => unwrapData(response.data));
  },
  async getQmsAdminList({ query = {} } = {}) {
    return this.getQmsListByRole({
      query: {
        ...query,
        isQMSAdmin: true
      }
    });
  },
  async listResponseTemplates() {
    return axios.get(`${configService.get('apiUrl')}/qms-response-template`).then(response => unwrapData(response.data));
  },
  async postResponseTemplates({ payload }) {
    return axios.post(`${configService.get('apiUrl')}/qms-response-template`, payload).then(response => unwrapData(response.data));
  },
  async patchResponseTemplate({ payload }) {
    return axios.patch(`${configService.get('apiUrl')}/qms-response-template`, payload).then(response => unwrapData(response.data));
  },
  async deleteResponseTemplate({ templateId }) {
    return axios.delete(`${configService.get('apiUrl')}/qms-response-template`, { data: { templateId } }).then(response => unwrapData(response.data));
  },
  /** GET /escalation-matrix/:key — full document (unwraps { success, data }) */
  async getEscalationMatrix({ key = 'hr_management' } = {}) {
    const url = `${configService.get('apiUrl')}/escalation-matrix/${key}`;
    return axios.get(url).then(response => unwrapData(response.data));
  },

  /** POST /escalation-matrix/:key — create body { key, isActive, matrix } */
  async postEscalationMatrix({ key = 'hr_management', payload }) {
    const url = `${configService.get('apiUrl')}/escalation-matrix/${key}`;
    return axios.post(url, payload).then(response => unwrapData(response.data));
  },

  /** PATCH /escalation-matrix/:key — same body shape as POST */
  async patchEscalationMatrix({ key = 'hr_management', payload }) {
    const url = `${configService.get('apiUrl')}/escalation-matrix/${key}`;
    return axios.patch(url, payload).then(response => unwrapData(response.data));
  },

  async getOne({ id }) {
    return axios
      .get(`${configService.get('apiUrl')}/qms/${id}`, {})
      .then(response => unwrapData(response.data));
  },

  async getOneQmsUserFromHono({ id }) {
    return axios
      .get(`${configService.get('apiUrl')}/qms-users/${id}`, {})
      .then(response => unwrapData(response.data));
  },

  async deleteOne({ id }) {
    return axios
      .delete(`${configService.get('apiUrl')}/qms/${id}`)
      .then(response => unwrapData(response.data));
  },

  async postOne({ payload } = {}) {
    return axios
      .post(`${configService.get('apiUrl')}/qms`, payload)
      .then(response => unwrapData(response.data));
  },

  async patchOne({ id, payload }) {
    return axios
      .patch(`${configService.get('apiUrl')}/qms/${id}`, payload)
      .then(response => unwrapData(response.data));
  },

  async patchOneByRole({ id, payload }) {
    return axios
      .patch(`${configService.get('apiUrl')}/qms-by-role/${id}`, payload)
      .then(response => unwrapData(response.data));
  },

  async getSignedUrl({ filename, filetype }) {
    const params = new URLSearchParams({
      filename: String(filename),
      filetype: String(filetype)
    });
    return axios
      .get(`${configService.get('apiUrl')}/qms/get-signed-url?${params.toString()}`, {})
      .then(response => response.data)
      .catch(e => {
        throw e;
      });
  },

  async uploadDocument({ url, file }) {
    const instance = axios.create();
    delete instance.defaults.headers.common.Authorization;
    const filetype = file.type;
    const config = {
      method: 'put',
      url,
      headers: {
        'Content-Type': filetype
      },
      data: file
    };
    return instance(config)
      .then(response => {
        return response.data;
      })
      .catch(e => {
        throw e;
      });
  }
};
